export const AWNING_PART_NUMBER = 'PS-KE-FULL-CASSETTE-AWNING';
export const AWNING_DISPLAY_NAME = 'PowerShades Full-Cassette Awning';

export type AwningOption = {
	index: number;
	value: string;
	is_default: boolean;
	/**
	 * True when the option must always be part of the selection and cannot be removed
	 * (e.g. the BOND MATE PRO remote). Only meaningful on multi-select properties.
	 */
	is_required: boolean;
};

export type AwningProperty = {
	property_number: number;
	property_name: string;
	is_multi_select: boolean;
	is_required: boolean;
	options: AwningOption[];
};

export type AwningFabric = {
	color_code: string;
	color_name: string;
	color_group: string;
	fabric_brand: string;
	ke_fabric_code: string;
	is_solid: boolean;
	is_stripe: boolean;
};

export type AwningConfigurationRequest = {
	select_unit_type: string;
	width: string;
	projection: string;
	arms_with_led: string;
	frame_color: string;
	mounting_option: string;
	roof_mount_brackets: string;
	mounting_brackets: string;
	drive_side: string;
	operation: string;
	motor_cable: string;
	remote: string[];
	sensors: string[];
	crank: string;
	fabric_brand: string;
	fabric_number: string;
	desired_pitch: string;
};

export type AwningPriceBreakdown = {
	base_price: number;
	bracket_adder: number;
	freight_adder: number;
	arms_with_led_adder: number;
	total_sensor_adder: number;
	sensor_adders: { sensor_option: string; adder: number }[];
	total_remote_adder: number;
	remote_adders: { remote_option: string; adder: number }[];
};

export type AwningPricingGroup = {
	breakdown: AwningPriceBreakdown;
	total_price: number;
};

export type AwningConfigurationResponse = {
	is_valid: boolean;
	errors: string[];
	msrp_pricing?: AwningPricingGroup;
	cost_pricing?: AwningPricingGroup;
	dealer_pricing?: AwningPricingGroup;
	distributor_pricing?: AwningPricingGroup;
};

export type AwningReleaseStatus =
	| 'released'
	| 'already_released'
	| 'already_transmitted'
	| 'not_found'
	| 'unknown';

/**
 * Response from POST Quotes/{quoteId}/Awning/Release.
 * Note: failures come back as HTTP 200 with an `error` key and no `success` field,
 * so `error` must be checked before anything else.
 */
export type AwningReleaseResponse = {
	success?: boolean;
	release_status?: AwningReleaseStatus;
	message?: string;
	hold_hours?: number;
	is_now_due?: boolean;
	error?: string;
};

export type AwningCommerceOrder = {
	id: number;
	sunsetter_po_number: string | null;
	status: string | null;
	ke_status: string | null;
	status_updated_at: string | null;
	order_released: boolean;
	order_date: string | null;
	captured_at: string | null;
	updated_at: string | null;
	transmitted_at: string | null;
	transmit_error: string | null;
	ke_order_number: string | null;
	ke_bol_number: string | null;
	ke_shipment_date: string | null;
	ke_shipment_tracking: string | null;
	ship_carrier: string | null;
	consumer_po: string | null;
	unit_count: number;
	items_known: number;
	items_shipped: number;
};

/**
 * Response from GET Quotes/{quoteId}/Awning/Order.
 * Same error convention as the release call - check `error` first.
 */
export type AwningOrderResponse = {
	success?: boolean;
	found?: boolean;
	can_release?: boolean;
	order?: AwningCommerceOrder | null;
	error?: string;
};

/** One row of GET Reports/AwningOrders - a non-archived awning quote with names resolved. */
export type AwningOrdersReportRow = {
	id: number;
	order_status: string | null;
	dealer_name: string | null;
	distributor_name: string | null;
	territory_name: string | null;
	cost_snapshot: number | null;
	dist_price_snapshot: number | null;
	dealer_price_snapshot: number | null;
};

/**
 * Response from GET Reports/AwningOrders.
 * Same error convention as the other awning calls - check `error` first.
 */
export type AwningOrdersReportResponse = {
	orders?: AwningOrdersReportRow[];
	error?: string;
};

export const PROPERTY_TO_CONFIG_FIELD: Record<number, keyof AwningConfigurationRequest> = {
	1: 'select_unit_type',
	2: 'width',
	3: 'projection',
	4: 'arms_with_led',
	5: 'frame_color',
	6: 'mounting_option',
	7: 'roof_mount_brackets',
	8: 'mounting_brackets',
	9: 'drive_side',
	10: 'operation',
	11: 'motor_cable',
	12: 'remote',
	14: 'crank',
	15: 'fabric_brand',
	16: 'fabric_number',
	17: 'desired_pitch',
};
